import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { logoPackages, brandingPackages } from "@/data/packages";
import { videoPackages } from "@/data/videoPackages";
import { brandingAddons } from "@/data/addons";

/**
 * CatalogProvider — fetches /api/catalog (packages + addons) and provides
 * helpers (findPackageByName, findAddonsByCategory, formatUSD).
 *
 * Falls back to code-defined defaults so the public site renders identically
 * even if the API is offline.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const toCatalog = (packages, addons) => ({ packages, addons });

// Map old JS data file shape -> new model shape (numeric base_price).
const legacyBranding = [
    ...logoPackages.map((p, i) => ({
        id: p.id, name: p.name, category: p.category, kind: "logo",
        tier: p.tier, base_price: p.priceFrom, price_label: p.priceLabel,
        price_note: p.priceNote || null, description: p.description,
        features: p.features, ribbon: p.ribbon, cta: p.cta,
        addons_category: p.addonsCategory || "branding", order: i, visible: true,
        accent_style: "default",
    })),
    ...brandingPackages.map((p, i) => ({
        id: p.id, name: p.name, category: p.category, kind: "branding",
        tier: p.tier, base_price: p.priceFrom, price_label: p.priceLabel,
        price_note: p.priceNote || null, description: p.description,
        features: p.features, ribbon: p.ribbon, cta: p.cta,
        addons_category: p.addonsCategory || "branding", order: 3 + i, visible: true,
        accent_style: p.tier >= 6 ? "apex" : p.tier >= 5 ? "flagship" : "default",
    })),
];

const legacyVideo = videoPackages.map((p, i) => ({
    id: p.id, name: p.name, category: p.category, kind: "video",
    tier: p.tier, base_price: p.priceFrom || p.price_from || 0, price_label: p.priceLabel,
    price_note: p.priceNote || null, description: p.description,
    features: p.features, ribbon: p.ribbon, cta: p.cta,
    addons_category: p.addonsCategory || "video", order: 6 + i, visible: true,
    accent_style: p.tier >= 4 ? "cool-flagship" : "default",
}));

const FALLBACK_PACKAGES = [...legacyBranding, ...legacyVideo];

const FALLBACK_ADDONS = brandingAddons.map((a, i) => ({
    id: a.id, name: a.name, description: a.description, price: a.price,
    category: "branding", applies_to_package_ids: [], order: i, visible: true,
}));

const CatalogContext = createContext({
    packages: FALLBACK_PACKAGES,
    addons: FALLBACK_ADDONS,
    loaded: false,
    refresh: () => {},
});

export const CatalogProvider = ({ children }) => {
    const [data, setData] = useState(toCatalog(FALLBACK_PACKAGES, FALLBACK_ADDONS));
    const [loaded, setLoaded] = useState(false);

    const refresh = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/catalog`, { timeout: 12000 });
            if (res.data?.packages?.length) {
                setData({
                    packages: res.data.packages,
                    addons: res.data.addons || FALLBACK_ADDONS,
                });
            }
        } catch (_err) {
            /* keep fallback */
        } finally {
            setLoaded(true);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const value = useMemo(() => ({ ...data, loaded, refresh }), [data, loaded, refresh]);
    return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
};

export const useCatalog = () => useContext(CatalogContext);

// ---- Helpers ----

export const formatUSD = (n) => {
    if (n === null || n === undefined || Number.isNaN(Number(n))) return "";
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
    }).format(Number(n));
};

export const findPackageByName = (packages, name) =>
    (packages || []).find((p) => p.name === name) || null;

export const findPackageById = (packages, id) =>
    (packages || []).find((p) => p.id === id) || null;

export const getAddonsForCategory = (addons, category) =>
    (addons || []).filter((a) => a.category === category && a.visible !== false);

export const getAddonsForPackage = (addons, pkg) => {
    if (!pkg) return [];
    const cat = pkg.addons_category || (pkg.kind === "video" ? "video" : "branding");
    const inCat = (addons || []).filter((a) => a.category === cat && a.visible !== false);
    return inCat.filter((a) =>
        !a.applies_to_package_ids || a.applies_to_package_ids.length === 0 || a.applies_to_package_ids.includes(pkg.id)
    );
};

export const computePricing = ({ selectedPackage, selectedAddons, isCustomScope }) => {
    const basePrice = isCustomScope ? 0 : Number(selectedPackage?.base_price || 0);
    const addOnsTotal = (selectedAddons || []).reduce((sum, a) => sum + Number(a.price || 0), 0);
    const estimatedTotal = isCustomScope ? null : basePrice + addOnsTotal;
    return { basePrice, addOnsTotal, estimatedTotal };
};

export default CatalogProvider;
